import axios from 'axios';
import { httpClient } from './http';
import { API } from '../config/api';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Add interceptor to include session token from localStorage as fallback
axios.interceptors.request.use((config) => {
  // If no Authorization header and we have a session token in localStorage, add it
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${sessionToken}`;
  }
  return config;
});

// Diet Plans
export const createPlan = (data) => httpClient.post(`${API}/plans`, data);
export const getActivePlan = () => httpClient.get(`${API}/plans/active`);
export const getPlans = () => httpClient.get(`${API}/plans`);
export const updatePlan = (planId, data) => httpClient.put(`${API}/plans/${planId}`, data);

// Weight Logs
export const logWeight = (data) => httpClient.post(`${API}/weight-logs`, data);
export const getWeightLogs = () => httpClient.get(`${API}/weight-logs`);
export const updateWeightLog = (logId, data) => httpClient.put(`${API}/weight-logs/${logId}`, data);
export const deleteWeightLog = (logId) => httpClient.delete(`${API}/weight-logs/${logId}`);

// Dashboard
export const getDashboard = () => httpClient.get(`${API}/dashboard`);

// Posts
export const createPost = (data) => axios.post(`${API}/posts`, data);

// Move (daily micro-workouts)
export const getMoveHome = () => httpClient.get(`${API}/move/home`);
export const generateMoveWorkout = (params) => httpClient.post(`${API}/move/generate`, params);
export const completeMoveSession = (data) => httpClient.post(`${API}/move/sessions`, data);
export const getMoveMusic = (mood) => httpClient.get(`${API}/move/music${mood ? `?mood=${encodeURIComponent(mood)}` : ''}`);
export const toggleMoveMusicFavorite = (trackId) => httpClient.post(`${API}/move/music/${trackId}/favorite`);
export const reportMoveMusicUsage = (seconds) => httpClient.post(`${API}/move/music/usage`, { seconds });
export const getPosts = (page = 1, filter = 'all') => axios.get(`${API}/posts?page=${page}&filter=${filter}`);
export const getChallengePosts = (challengeId) => axios.get(`${API}/posts/challenge/${challengeId}`);
export const addReaction = (postId, emoji) => axios.post(`${API}/posts/${postId}/reactions`, { emoji });
export const getPostReactionUsers = (postId) => axios.get(`${API}/posts/${postId}/reactions/users`);
export const addCommentReaction = (postId, commentId, emoji) =>
  axios.post(`${API}/posts/${postId}/comments/${commentId}/reactions`, { emoji });
export const changePostVisibility = (postId, challengeIds) =>
  axios.put(`${API}/posts/${postId}/visibility`, { challenge_ids: challengeIds });
export const addComment = (postId, text) => axios.post(`${API}/posts/${postId}/comments`, { text });
export const deletePost = (postId) => axios.delete(`${API}/posts/${postId}`);
export const deleteComment = (postId, commentId) => axios.delete(`${API}/posts/${postId}/comments/${commentId}`);

// Challenges
export const createChallenge = (data) => axios.post(`${API}/challenges`, data);
export const getChallenges = () => axios.get(`${API}/challenges`);
export const getChallenge = (id) => axios.get(`${API}/challenges/${id}`);
export const joinChallenge = (code) => axios.post(`${API}/challenges/join`, { code });
export const getPublicChallenges = () => axios.get(`${API}/challenges/public`);
export const joinPublicChallenge = (challengeId) => axios.post(`${API}/challenges/${challengeId}/join`);
export const reactivateChallenge = (challengeId) => axios.post(`${API}/challenges/${challengeId}/reactivate`);
export const leaveChallenge = (challengeId) => axios.post(`${API}/challenges/${challengeId}/leave`);
export const deleteChallenge = (challengeId) => axios.delete(`${API}/challenges/${challengeId}`);
export const updateChallenge = (challengeId, data) => axios.patch(`${API}/challenges/${challengeId}`, data);
export const removeChallengeParticipant = (challengeId, userId) => axios.delete(`${API}/challenges/${challengeId}/participants/${userId}`);
export const notifyWorkoutStarted = (type = 'walk') => axios.post(`${API}/workouts/started`, { type });
export const workoutHeartbeat = (payload) => axios.post(`${API}/workouts/heartbeat`, payload);
export const stopWorkoutHeartbeat = () => axios.post(`${API}/workouts/heartbeat/stop`);
export const getActiveWorkoutPeers = () => axios.get(`${API}/workouts/active-peers`);
export const sendWalkieTalkie = (audioBase64, mimeType = 'audio/webm') =>
  axios.post(`${API}/workouts/walkie-talkie`, { audio_base64: audioBase64, mime_type: mimeType });
export const ackWalkieTalkie = (msgId) =>
  axios.post(`${API}/workouts/walkie-talkie/${msgId}/played`);

// Live media (photo / short video) shared during an active workout —
// visible only to challenge peers also live, ephemeral 30-min TTL.
export const sendLiveMedia = (mediaBase64, mediaType, mimeType, durationSec) =>
  axios.post(`${API}/workouts/live-media`, {
    media_base64: mediaBase64,
    media_type: mediaType,
    mime_type: mimeType,
    duration_sec: durationSec,
  });
export const getLiveMediaByOwner = (ownerUserId) =>
  axios.get(`${API}/workouts/live-media/by-owner/${ownerUserId}`);
export const ackLiveMedia = (mediaId) =>
  axios.post(`${API}/workouts/live-media/${mediaId}/viewed`);

// User
export const updateUser = (data) => axios.put(`${API}/users/me`, data);
export const getUserBadges = () => axios.get(`${API}/users/me/badges`);
export const uploadProfilePhoto = (imageBase64) => axios.post(`${API}/upload/profile-photo`, { image: imageBase64 });

// Notifications
export const getNotifications = () => axios.get(`${API}/notifications`);
export const markAllRead = () => axios.post(`${API}/notifications/read-all`);
export const getUnreadCount = () => axios.get(`${API}/notifications/unread-count`);

// Badges
export const getAllBadges = () => axios.get(`${API}/badges`);

// AI Chef
export const generateRecipe = (mealType = 'pranzo', language = 'it') => axios.post(`${API}/chef/generate-recipe`, { meal_type: mealType, language: language });
export const publishRecipe = (recipe, imageUrl, challengeId) => axios.post(`${API}/chef/publish-recipe`, { recipe, image_url: imageUrl, challenge_id: challengeId });
export const getChefRecipes = () => axios.get(`${API}/chef/recipes`);

// Walk Tracker
export const getWalkRecommendation = () => axios.get(`${API}/walk/recommendation`);

// Meal Plan
export const generateMealPlan = (language = 'it', allergies = '', preferences = '') => 
  axios.post(`${API}/meal-plan/generate`, { language, allergies, preferences }, { timeout: 60000 });
export const generateMealPlanAsync = (language = 'it', allergies = '', preferences = '') =>
  axios.post(`${API}/meal-plan/generate-async`, { language, allergies, preferences });
export const getCurrentMealPlan = () => axios.get(`${API}/meal-plan/current`);
export const getMealPlanHistory = () => axios.get(`${API}/meal-plan/history`);
export const getMealPlanStatus = (planId) => axios.get(`${API}/meal-plan/status/${planId}`);
export const getShoppingList = (days = 7) => axios.post(`${API}/meal-plan/shopping-list`, { days });

// Files - get URL for images stored in object storage
export const getFileUrl = (path) => `${API}/files/${path}`;

// Admin APIs
export const adminGetStats = () => axios.get(`${API}/admin/stats`);
export const adminGetUsers = (page = 1, search = '') => axios.get(`${API}/admin/users?page=${page}&search=${search}`);
export const adminGetUserDetail = (userId) => axios.get(`${API}/admin/users/${userId}`);
export const adminBanUser = (userId, banned) => axios.put(`${API}/admin/users/${userId}/ban`, { banned });
export const adminToggleAdmin = (userId, isAdmin) => axios.put(`${API}/admin/users/${userId}/admin`, { is_admin: isAdmin });
export const adminWatchUser = (userId, isWatched, reason = null) => axios.post(`${API}/admin/users/watch`, { user_id: userId, is_watched: isWatched, reason });
export const adminBanEmail = (email, reason = null) => axios.post(`${API}/admin/emails/ban`, { email, reason });
export const adminUnbanEmail = (emailHash) => axios.delete(`${API}/admin/emails/ban/${emailHash}`);
export const adminGetBannedEmails = () => axios.get(`${API}/admin/emails/banned`);
export const adminGetWatchedUsers = () => axios.get(`${API}/admin/users/watched`);
export const adminDeleteUser = (userId) => axios.delete(`${API}/admin/users/${userId}`);
export const adminGetPosts = (page = 1, postType = '') => axios.get(`${API}/admin/posts?page=${page}&post_type=${postType}`);
export const adminDeletePost = (postId) => axios.delete(`${API}/admin/posts/${postId}`);
export const adminGetReports = (status = 'pending', page = 1) => axios.get(`${API}/admin/reports?status=${status}&page=${page}`);
export const adminHandleReport = (reportId, action) => axios.put(`${API}/admin/reports/${reportId}`, { action });
export const adminGetMessageReports = (status = 'pending', page = 1) =>
  axios.get(`${API}/admin/messages/reports?status=${status}&page=${page}`);
export const adminHandleMessageReport = (reportId, action) =>
  axios.put(`${API}/admin/messages/reports/${reportId}`, { action });
export const adminGetMessageReportContext = (reportId) =>
  axios.get(`${API}/admin/messages/reports/${reportId}/context`);

// Payment providers (Stripe / Apple IAP / Google Play)
export const getPaymentsConfig = () => axios.get(`${API}/payments/config`);
export const adminGetPaymentsConfig = () => axios.get(`${API}/payments/admin/config`);
export const adminUpdatePaymentsConfig = (patch) =>
  axios.put(`${API}/payments/admin/config`, patch);
export const restorePurchases = () => axios.post(`${API}/payments/restore`);
export const applePurchaseComplete = (payload) =>
  axios.post(`${API}/payments/apple/complete`, payload);
export const googlePurchaseValidate = (payload) =>
  axios.post(`${API}/payments/google/validate`, payload);
export const adminGenerateRecipe = (mealType, autoPublish = false) => axios.post(`${API}/admin/chef/generate`, { meal_type: mealType, auto_publish: autoPublish });
export const reportPost = (postId, reason) => axios.post(`${API}/posts/report`, { post_id: postId, reason });
export const withdrawReport = (postId) => axios.delete(`${API}/posts/${postId}/report`);

// Advertising
export const getAdConfig = () => axios.get(`${API}/ads/config`);
export const getBanner = (position) => axios.get(`${API}/ads/banner/${position}`);
export const trackBannerClick = (bannerId) => axios.post(`${API}/ads/banner/${bannerId}/click`);

// Admin Advertising
export const adminGetBanners = (position = '') => axios.get(`${API}/admin/ads/banners${position ? `?position=${position}` : ''}`);
export const adminCreateBanner = (data) => axios.post(`${API}/admin/ads/banners`, data);
export const adminUpdateBanner = (bannerId, data) => axios.put(`${API}/admin/ads/banners/${bannerId}`, data);
export const adminDeleteBanner = (bannerId) => axios.delete(`${API}/admin/ads/banners/${bannerId}`);
export const adminGetAdConfig = () => axios.get(`${API}/admin/ads/config`);
export const adminUpdateAdConfig = (data) => axios.put(`${API}/admin/ads/config`, data);
export const adminGetAdStats = () => axios.get(`${API}/admin/ads/stats`);

// Workouts
export const saveWorkout = (data) => axios.post(`${API}/workouts`, data);
export const getMyWorkouts = () => axios.get(`${API}/workouts`);
export const deleteWorkout = (workoutId) => axios.delete(`${API}/workouts/${workoutId}`);

// My Recipes
export const getMyRecipes = (category = '') => axios.get(`${API}/chef/my-recipes${category ? `?category=${category}` : ''}`);
export const saveRecipe = (recipeData) => axios.post(`${API}/chef/save-recipe`, recipeData);

// Premium
export const getPremiumStatus = () => axios.get(`${API}/premium/status`);

// Champion System
export const becomeChampion = (achievement) => axios.post(`${API}/social/champion/become`, { achievement });
export const getChampions = (limit = 20) => axios.get(`${API}/social/champions?limit=${limit}`);
export const getChampionProfile = (userId) => axios.get(`${API}/social/champion/${userId}`);
export const getFeaturedMotivationalPost = () => axios.get(`${API}/posts/motivational/featured`);

// Admin — transactions & analytics
export const adminListTransactions = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')).toString();
  return axios.get(`${API}/admin/transactions${qs ? `?${qs}` : ''}`);
};
export const adminTransactionsSummary = (days = 30) => axios.get(`${API}/admin/transactions/summary?days=${days}`);
export const adminStatsOverview = () => axios.get(`${API}/admin/stats/overview`);
export const adminStatsAudience = () => axios.get(`${API}/admin/stats/audience`);
export const adminStatsGrowth = () => axios.get(`${API}/admin/stats/growth`);
export const adminStatsRevenue = () => axios.get(`${API}/admin/stats/revenue`);
export const adminStatsFeatures = () => axios.get(`${API}/admin/stats/features`);
export const adminStatsHealth = () => axios.get(`${API}/admin/stats/health`);
export const adminStatsActivityHeatmap = (days = 30) => axios.get(`${API}/admin/stats/activity-heatmap?days=${days}`);
export const adminStatsGeography = () => axios.get(`${API}/admin/stats/geography`);
export const adminStatsClearCache = () => axios.post(`${API}/admin/stats/cache/clear`);
export const adminRefundTransaction = (transactionId) =>
  axios.post(`${API}/admin/transactions/${transactionId}/refund`);
export const adminGetQueueStatus = () => axios.get(`${API}/admin/queue/status`);
export const adminRetryQueueJob = (jobId) => axios.post(`${API}/admin/queue/jobs/${jobId}/retry`);
export const adminListBroadcasts = (status) =>
  axios.get(`${API}/admin/broadcasts${status ? `?status=${status}` : ''}`);
export const adminCancelBroadcast = (broadcastId) =>
  axios.delete(`${API}/admin/broadcasts/${broadcastId}`);
export const adminListFailedJobs = (limit = 50) => axios.get(`${API}/admin/queue/failed-jobs?limit=${limit}`);
export const adminRetryFailedJob = (id) => axios.post(`${API}/admin/queue/failed-jobs/${id}/retry`);
export const adminDeleteFailedJob = (id) => axios.delete(`${API}/admin/queue/failed-jobs/${id}`);

// Private Messaging
export const getMessagesPrivacy = () => axios.get(`${API}/messages/privacy`);
export const setMessagesPrivacy = (messages_privacy) =>
  axios.put(`${API}/messages/privacy`, { messages_privacy });
export const canMessageUser = (userId) => axios.get(`${API}/messages/can-message/${userId}`);
export const sendMessage = (payload) => axios.post(`${API}/messages/send`, payload);
export const getConversations = (folder = 'inbox') =>
  axios.get(`${API}/messages/conversations?folder=${folder}`);
export const getConversation = (conversationId, before = null, limit = 50) => {
  const qs = new URLSearchParams();
  if (before) qs.set('before', before);
  qs.set('limit', String(limit));
  return axios.get(`${API}/messages/conversations/${conversationId}?${qs.toString()}`);
};
export const markConversationRead = (conversationId) =>
  axios.post(`${API}/messages/conversations/${conversationId}/read`);
export const acceptConversation = (conversationId) =>
  axios.post(`${API}/messages/conversations/${conversationId}/accept`);
export const declineConversation = (conversationId) =>
  axios.post(`${API}/messages/conversations/${conversationId}/decline`);
export const deleteConversation = (conversationId) =>
  axios.delete(`${API}/messages/conversations/${conversationId}`);
export const deleteMessage = (messageId) => axios.delete(`${API}/messages/${messageId}`);
export const reportMessage = (messageId, reason) =>
  axios.post(`${API}/messages/${messageId}/report`, { reason });
export const getMessagesUnreadCount = () => axios.get(`${API}/messages/unread-count`);

